import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BookOpen,
  Calculator,
  Code2,
  FileCheck2,
  GitCompareArrows,
  ShieldCheck,
} from 'lucide-react'

import { BlogPostCard } from '@/components/blog/blog-post-card'
import { JsonLd } from '@/components/seo/json-ld'
import { SeoHead } from '@/components/seo/seo-head'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { SectionHeading } from '@/components/ui/section-heading'
import { FREE_TOOLS } from '@/data/free-tools'
import { getPublicBlogIndexData } from '@/lib/blog/server'
import { createMetadata, getBreadcrumbSchema, marketingSeoPages } from '@/lib/seo'

const startHere = [
  {
    icon: GitCompareArrows,
    eyebrow: 'Evaluate',
    title: 'Choose a donation platform',
    description: 'Compare pricing models, donor checkout, website fit, and operational tradeoffs using cited facts.',
    href: '/compare',
    cta: 'Open comparisons',
  },
  {
    icon: Code2,
    eyebrow: 'Build',
    title: 'Put giving on your website',
    description: 'Follow platform-specific installation guidance for WordPress, Webflow, Squarespace, Wix, and more.',
    href: '/integrations',
    cta: 'Browse integrations',
  },
  {
    icon: Calculator,
    eyebrow: 'Plan',
    title: 'Run the numbers first',
    description: 'Model processing fees, recurring giving, campaign gift ranges, sponsorships, receipts, and year-end work.',
    href: '/tools',
    cta: 'Use the free tools',
  },
] as const

const flagshipResources = [
  {
    label: 'Flagship comparison',
    title: 'KindLumen vs. Zeffy',
    description: 'A balanced decision guide with official sources, explicit assumptions, limitations, and a visible verification date.',
    href: '/compare/kindlumen-vs-zeffy',
  },
  {
    label: 'Interactive analysis',
    title: 'Donation fee calculator',
    description: 'Change the gift amount and published rates yourself. Variable donor tips are disclosed, not guessed.',
    href: '/tools/donation-fee-calculator',
  },
  {
    label: 'WordPress implementation',
    title: 'KindLumen donation forms for WordPress',
    description: 'See the plugin, shortcodes, block, campaign cards, and the handoff path before installing anything.',
    href: '/wordpress-plugin',
  },
] as const

const curatedGuideSlugs = [
  'how-to-add-a-stripe-donation-form-to-wordpress-with-the-kindlumen-plugin',
  'stripe-donations-without-writing-code',
] as const

const metadata = createMetadata(marketingSeoPages.resources)

export default function ResourcesPage() {
  const blogIndexData = getPublicBlogIndexData()
  const bySlug = new Map(blogIndexData.posts.map((post) => [post.slug, post]))
  const guideCards = curatedGuideSlugs
    .map((slug) => bySlug.get(slug))
    .filter((post): post is NonNullable<typeof post> => Boolean(post))

  return (
    <div className="space-y-20 pb-20 sm:space-y-24">
      <SeoHead metadata={metadata} />
      <JsonLd
        data={getBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Resources', path: '/resources' },
        ])}
      />

      <section className="overflow-hidden bg-[linear-gradient(180deg,#EEF7FF_0%,#F8FCFF_62%,#FFFFFF_100%)]">
        <div className="marketing-shell py-20 sm:py-24">
          <div className="grid gap-10 xl:grid-cols-[1.02fr_0.98fr] xl:items-center">
            <div>
              <div className="eyebrow">KindLumen resources</div>
              <h1 className="mt-6 max-w-4xl text-balance text-[2.55rem] font-semibold leading-[1.07] sm:text-[3.65rem] sm:leading-[1.03]">
                Make the next fundraising decision with the evidence attached.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
                Practical implementation guides, maintained platform comparisons, and tools that do useful work before you create an account.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg">
                  <Link to="/compare">
                    Compare platforms
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="secondary">
                  <Link to="/tools">Use a free tool</Link>
                </Button>
              </div>
            </div>

            <div className="panel-dark p-7 sm:p-9">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/55">How we publish</p>
              <h2 className="mt-4 max-w-xl text-3xl font-semibold leading-[1.14] text-white">
                Useful even if you never choose KindLumen.
              </h2>
              <div className="mt-7 grid gap-3">
                {[
                  'Official sources for volatile pricing and product facts',
                  'Clear assumptions instead of manufactured certainty',
                  'Competitor strengths and KindLumen limitations included',
                  'Dates changed only when the substance changes',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-[20px] border border-white/10 bg-white/[0.07] px-4 py-3.5">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" />
                    <p className="text-sm leading-6 text-white/75">{item}</p>
                  </div>
                ))}
              </div>
              <Link to="/editorial-standards" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-sky-200">
                Read our editorial standards
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading
          eyebrow="Start with the job"
          title="Three paths through the library."
          description="Choose the task in front of you, then go straight to the comparison, guide, or working tool that helps."
        />
        <div className="mt-9 grid gap-6 lg:grid-cols-3">
          {startHere.map((item) => (
            <Link key={item.title} to={item.href} className="group">
              <Card className="h-full transition duration-200 group-hover:-translate-y-1 group-hover:border-brand/25 group-hover:shadow-[0_24px_60px_rgba(15,30,68,0.1)]">
                <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                  <item.icon className="h-5 w-5" />
                </div>
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-brand">{item.eyebrow}</p>
                <CardTitle className="mt-2 text-2xl">{item.title}</CardTitle>
                <CardDescription className="mt-4">{item.description}</CardDescription>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                  {item.cta}
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel p-7 sm:p-9 lg:p-11">
          <SectionHeading
            eyebrow="Featured work"
            title="Start with the resources that carry the most evidence."
            description="These pages show the standard we are applying as the library is reviewed and rebuilt."
          />
          <div className="mt-9 grid gap-5 lg:grid-cols-3">
            {flagshipResources.map((resource) => (
              <Link
                key={resource.href}
                to={resource.href}
                className="group rounded-[24px] border border-line bg-white p-6 transition hover:border-brand/25 hover:shadow-soft"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.17em] text-brand">{resource.label}</p>
                <h3 className="mt-4 text-2xl font-semibold leading-[1.16] text-ink">{resource.title}</h3>
                <p className="mt-4 text-sm leading-6 text-ink-soft">{resource.description}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand">
                  Open resource
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading
          eyebrow="Implementation guides"
          title="Start with Stripe or WordPress."
          description="Two practical guides for choosing the right Stripe path and installing KindLumen on a WordPress site."
          actions={
            <Button asChild variant="secondary">
              <Link to="/blog">
                Browse all guides
                <BookOpen className="h-4 w-4" />
              </Link>
            </Button>
          }
        />
        <div className="mt-9 grid gap-6 lg:grid-cols-2">
          {guideCards.map((guide) => (
            <BlogPostCard key={guide.slug} post={guide} compact />
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading
          eyebrow="Free working tools"
          title="Do the work here, without signing up."
          description="Calculators, generators, and trackers run in your browser. Open the full library for privacy notes and export options."
          actions={
            <Button asChild variant="secondary">
              <Link to="/tools">View all {FREE_TOOLS.length} tools</Link>
            </Button>
          }
        />
        <div className="mt-9 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {FREE_TOOLS.slice(0, 6).map((tool) => (
            <Link key={tool.href} to={tool.href} className="group rounded-[24px] border border-line bg-white p-6 shadow-soft transition hover:border-brand/30">
              <div className="flex items-start justify-between gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-[16px] bg-brand/10 text-brand">
                  <tool.icon className="h-5 w-5" />
                </div>
                <ArrowRight className="h-4 w-4 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-brand" />
              </div>
              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">{tool.audience}</p>
              <h3 className="mt-2 text-xl font-semibold leading-[1.2] text-ink">{tool.title}</h3>
              <p className="mt-3 text-sm leading-6 text-ink-soft">{tool.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel-dark overflow-hidden p-8 sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
                <FileCheck2 className="h-3.5 w-3.5" />
                Ready to test the setup?
              </div>
              <h2 className="mt-6 max-w-2xl text-balance text-3xl font-semibold leading-[1.14] text-white sm:text-4xl">
                Build the form, then make a real test donation.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-white/70">
                KindLumen is free for organizations, connects to your own Stripe account, and supports one-time and recurring giving on the website you already have.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button asChild size="lg">
                <Link to="/signup">Create a free form</Link>
              </Button>
              <Button asChild size="lg" variant="dark">
                <Link to="/pricing">Review the fee model</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export const Component = ResourcesPage
